import React, { useEffect, useState } from "react";
import BottomSheet from "../components/BottomSheet";
import FaCandle from "../icons/FaCandle";
import FaReport from "../icons/FaReport";
import { TbAntennaBars5 } from "react-icons/tb";
import { FiRefreshCw } from "react-icons/fi";
import ProfileService from "../../../services/Profile";

const PackageSheet = ({ openPackages, setOpenPackages }) => {
  const [pkg, setPkg] = useState([]);
  const [loading, setLoading] = useState(false);

  async function getProfile() {
    try {
      setLoading(true);
      const res = await ProfileService.getProfile();
      if (res?.status === 200) {
        setPkg(res.data?.data?.finduser?.signal_package || []);
      }
    } catch (err) {
      console.error("getProfile error:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (openPackages) getProfile();
  }, [openPackages]);

  function getPackageInfo(item) {
  
    const durationDays = Number(item?.duration?.days || 0);

    const expirationIso = item?.expirationtime || item?.updatedAt;
    const expireDate = expirationIso ? new Date(expirationIso) : null;
    const today = new Date();

    let remainingDays = null;
    if (expireDate) {
      const diffMs = expireDate.setHours(0,0,0,0) - today.setHours(0,0,0,0);
      remainingDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    } else {
      remainingDays = durationDays;
    }

    if (remainingDays < 0) remainingDays = 0;
    const daysPassed = Math.max(0, durationDays - remainingDays);
    let percentRemaining = durationDays > 0 ? ((durationDays - daysPassed) / durationDays) * 100 : 0;
    percentRemaining = Math.max(0, Math.min(100, percentRemaining)); 

    let status = "active"; // active | expiring | expired
    if (remainingDays <= 0) status = "expired";
    else if (remainingDays <= 4) status = "expiring";

    const pad = (n) => String(n).padStart(2, "0");
    const expireDateFormatted = expireDate
      ? `${expireDate.getFullYear()}/${pad(expireDate.getMonth() + 1)}/${pad(expireDate.getDate())}`
      : "-";

    return {
      remainingDays,
      expireDateFormatted,
      percentRemaining,
      status,
      durationDays,
    };
  }

  return (
    <>
      <BottomSheet open={openPackages} onClose={() => setOpenPackages(false)} title="Signal Packages">
        <div className="space-y-4 flex flex-col">
          <div className="flex justify-end">
            <button
              onClick={getProfile}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-white/80 shadow"
              disabled={loading}
            >
              <FiRefreshCw className={loading ? "animate-spin" : ""} />
              <span className="text-sm">{loading ? "...Loading" : "Refresh"}</span>
            </button>
          </div>

          {pkg?.length === 0 && (
            <div className="text-center text-sm text-gray-500 py-6">No packages found.</div>
          )}

          {pkg?.map((it, index) => {
            const info = getPackageInfo(it);
            const key = it?._id || `${index}-${it?.package?._id || "p"}`;

            let containerClass = "bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200";
            let badge = { text: "Active", className: "bg-green-100 text-green-700" };
            let progressGradient = "from-yellow-400 to-orange-400";
            let daysTextClass = "text-gray-900";

            if (info.status === "expiring") {
              containerClass = "bg-gradient-to-r from-red-50 to-pink-100 border border-red-200";
              badge = { text: "Expiring Soon", className: "bg-orange-100 text-orange-700" };
              progressGradient = "from-red-400 to-red-500";
              daysTextClass = "text-red-600";
            } else if (info.status === "expired") {
              containerClass = "bg-gray-50 border border-gray-200";
              badge = { text: "Expired", className: "bg-gray-200 text-gray-700" };
              progressGradient = "from-gray-300 to-gray-500";
              daysTextClass = "text-gray-500";
            }

            return (
              <div key={key} className={`${containerClass} rounded-2xl p-4`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center ml-3 ${
                        info.status === "expiring" ? "bg-red-600" : info.status === "expired" ? "bg-gray-400" : "bg-blue-600"
                      }`}
                    >
                      {info.status === "expiring" ? (
                        <FaCandle className="text-white" />
                      ) : info.status === "expired" ? (
                        <FaReport className="text-white" />
                      ) : (
                        <TbAntennaBars5 size={20} className="text-white" />
                      )}
                    </div>

                    <div>
                      <h4 className="font-bold text-gray-900">{it?.package?.name || it?.name || "Unnamed"}</h4>
                      <p className="text-sm text-gray-600 whitespace-nowrap truncate w-5/6">
                        {it?.package?.description || it?.description || "No description"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`${badge.className} px-3 py-1 rounded-full text-xs font-medium`}>
                      {badge.text}
                    </span>

                    <button
                      onClick={() => {
                        getProfile();
                      }}
                      className="w-8 h-8 bg-white/80 rounded-lg flex items-center justify-center"
                      title="Refresh"
                    >
                      <FiRefreshCw className={`${info.status === "expiring" ? "text-red-600" : info.status === "expired" ? "text-gray-400" : "text-blue-600"} text-sm`} />
                    </button>
                  </div>
                </div>

                <div className="w-full bg-white/60 rounded-full h-2 mb-3 overflow-hidden">
                  <div
                    className={`h-2 rounded-full bg-gradient-to-r ${progressGradient}`}
                    style={{ width: `${info.percentRemaining}%` }}
                  />
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Expires: {info.expireDateFormatted}</span>
                  <span className={`font-medium ${daysTextClass}`}>
                    {info.status === "expired" ? "Expired" : `${info.remainingDays} days remaining`}
                  </span>
                </div>
              </div>
            );
          })}

        </div>
      </BottomSheet>
    </>
  );
};

export default PackageSheet;